import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import chevronIcon from '../../assets/icons/chevron-small.svg';
import './page-header.css';

/**
 * Bouton d'action de l'en-tête (secondaire par défaut, primaire en option)
 */
export const PageHeaderButton = React.forwardRef(
  (
    { children, glyph, badge, chevron = false, primary = false, ...props },
    ref
  ) => (
    <button
      type="button"
      ref={ref}
      className={`agv-page-header__btn ${
        primary ? 'agv-page-header__btn--primary' : ''
      }`}
      {...props}
    >
      {glyph && <span className="agv-page-header__btn-glyph">{glyph}</span>}
      {children}
      {badge > 0 && <span className="agv-page-header__btn-badge">{badge}</span>}
      {chevron && (
        <img
          src={chevronIcon}
          alt=""
          width="6.06218"
          height="3.375"
          className="agv-page-header__btn-chevron"
        />
      )}
    </button>
  )
);
PageHeaderButton.displayName = 'PageHeaderButton';

/**
 * En-tête de page v4 : fil d'Ariane, icône, titre, compteur, chiffres clés,
 * actions à droite et onglets de vue
 *
 * @param {string[]} breadcrumb - libellés du fil d'Ariane, le dernier est la page courante
 * @param {object} icon - icône FontAwesome de la page, affichée sur fond vert clair
 * @param {Array} tabs - { key, icon, label, count, countTone, separatorBefore, disabled, title }
 *   countTone : 'warning' (défaut, ambre) ou 'neutral' (gris)
 */
export default function PageHeader({
  breadcrumb = [],
  icon,
  title,
  count,
  summary,
  actions,
  tabs = [],
  activeTab,
  onTabChange,
}) {
  return (
    <div className="agv-page-header">
      {breadcrumb.length > 0 && (
        <div className="agv-page-header__breadcrumb">
          {breadcrumb.map((label, index) => {
            const isLast = index === breadcrumb.length - 1;
            return (
              <React.Fragment key={label}>
                {index > 0 && (
                  <span className="agv-page-header__breadcrumb-sep">/</span>
                )}
                <span
                  className={
                    isLast ? 'agv-page-header__breadcrumb-current' : undefined
                  }
                >
                  {label}
                </span>
              </React.Fragment>
            );
          })}
        </div>
      )}

      <div className="agv-page-header__title-row">
        <div className="agv-page-header__title-left">
          <span className="agv-page-header__entity-icon">
            {icon ? (
              <FontAwesomeIcon icon={icon} />
            ) : (
              <span className="agv-page-header__entity-placeholder" />
            )}
          </span>
          <h1 className="agv-page-header__title">{title}</h1>
          {count !== undefined && count !== null && (
            <span className="agv-page-header__count">{count}</span>
          )}
          {summary && (
            <span className="agv-page-header__summary">{summary}</span>
          )}
        </div>
        {actions && <div className="agv-page-header__actions">{actions}</div>}
      </div>

      {tabs.length > 0 && (
        <div className="agv-page-header__tabs" role="tablist">
          {tabs.map((tab) => (
            <React.Fragment key={tab.key}>
              {tab.separatorBefore && (
                <span className="agv-page-header__tab-separator" />
              )}
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === tab.key}
                className={`agv-page-header__tab ${
                  activeTab === tab.key ? 'agv-page-header__tab--active' : ''
                }`}
                disabled={tab.disabled}
                title={tab.title}
                onClick={() => onTabChange(tab.key)}
              >
                {tab.icon && (
                  <span className="agv-page-header__tab__icon">{tab.icon}</span>
                )}
                <span className="agv-page-header__tab__label">{tab.label}</span>
                {tab.count > 0 && (
                  <span
                    className={`agv-page-header__tab__count ${
                      tab.countTone === 'neutral'
                        ? 'agv-page-header__tab__count--neutral'
                        : ''
                    }`}
                  >
                    {tab.count}
                  </span>
                )}
              </button>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}
